import { SurveyActionDialogComponent } from './survey-action-dialog/survey-action-dialog.component';
import { CallbackActionDialogComponent } from './callback-action-dialog/callback-action-dialog.component';
import { Injectable } from '@angular/core';
import { TabConfig } from '@optsolservices/ngx-data-table-tab-group';
import { Request } from '@optsolservices/ngx-data-table-with-search-criteria';
import { DetailSectionConfig } from '@optsolservices/ngx-detail-section';
import { Observable } from 'rxjs';
import { ApiResponse } from '../core/models/api-response';
import { BaseService } from '../core/services/base.service';
import { HelperService } from '../core/services/helper.service';
import { GeneralStoreService } from '../core/store/general.store';
import { DetailPageRouteParams } from '../generic/models/detail-page-route-params';
import { LandingPageRouteParams } from '../generic/models/landing-page-route-params';
import { PortalContext } from '../generic/models/portal-context';
import { ActionEftRequestDialogComponent } from '../pos/components/action-eft-request-dialog/action-eft-request-dialog.component';
import { FinanceFreeMoneyToAccountDialogComponent } from '../pos/components/finance-free-money-to-account-dialog/finance-free-money-to-account-dialog.component';
import { GtrReceiveDialogComponent } from '../pos/components/gtr-receive-dialog/gtr-receive-dialog.component';
import { PosAccountPayWithMoneyEquipmentDialogComponent } from '../pos/components/pos-account-pay-with-money-equipment-dialog/pos-account-pay-with-money-equipment-dialog.component';
import { PosCashUpReportDialogComponent } from '../pos/components/pos-cash-up-report-dialog/pos-cash-up-report-dialog.component';
import { PosOperatorTransactionsReportDialogComponent } from '../pos/components/pos-operator-transactions-report-dialog/pos-operator-transactions-report-dialog.component';
import { SctConfirmDialogComponent } from '../pos/components/sct-confirm-dialog/sct-confirm-dialog.component';
import { SctSearchStockCodeDialogComponent } from '../pos/components/sct-search-stock-code-dialog/sct-search-stock-code-dialog.component';
import { SalesOrderCreateDialogComponent } from '../pos/sales-orders/sales-order-create-dialog/sales-order-create-dialog.component';
import { SalesOrderCustomerDialogComponent } from '../pos/sales-orders/sales-order-customer-dialog/sales-order-customer-dialog.component';
import { IncomingMessageBinariesDialogComponent } from './dynamic-dialogs/incoming-message-binaries/incoming-message-binaries-dialog.component';
import { OutgoingResultBinariesDialogComponent } from './dynamic-dialogs/outgoing-result-binaries-dialog/outgoing-result-binaries-dialog.component';
import { UserMenuPreviewDialogComponent } from './dynamic-dialogs/user-menu-preview-dialog/user-menu-preview-dialog.component';
import { EmailFailedDeliveryActionDialogComponent } from './email-failed-delivery-action-dialog/email-failed-delivery-action-dialog.component';
import { ToDoListItemActionDialogComponent } from './todo-list-item-action-dialog/todo-list-item-action-dialog.component';

export const DEFAULT_DATE_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'yyyy-MM-DD',
    monthYearLabel: 'yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'yyyy',
  },
};

export const DEFAULT_MONTH_FORMATS = {
  parse: {
    dateInput: 'yyyy-MM',
  },
  display: {
    dateInput: 'yyyy-MM',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export interface DataTable {
  tableConfig: any;
  tableData: any;
}

export interface TableRequest {
  searchCriteriaConfigRequest: Request;
  tableConfigRequest: Request;
  tableDataRequest: Request;
}

export interface DataTableMasterDetail extends TableRequest {
  tabConfig: TabConfig[];
}

export interface DetailSectionMasterDetail {
  detailSectionConfig: DetailSectionConfig;
  tabConfig: TabConfig[];
}

@Injectable({
  providedIn: 'root'
})
export class SharedService {

  constructor(
    private helperService: HelperService,
    private baseService: BaseService,
    private generalStore: GeneralStoreService,
  ) {

  }

  /*
    Application Environment Settings
  */



  getPageConfig(routeParams: LandingPageRouteParams | DetailPageRouteParams, portalContext: PortalContext): Observable<ApiResponse<any>> {
    return this.baseService.executeCommand<any>('portal_generic_page', Object.assign(routeParams, { portalContext }));
  }

  async getDataTable(gridConfigRequest: string, gridDataRequest: string, gridDataReqestPayload?: any): Promise<DataTable> {
    let ownerId = this.generalStore.getSelectedOwnerValue().id;
    let dataTableConfig = (await this.baseService.executeCommand<any>(gridConfigRequest, { ownerId }).toPromise()).data;
    let dataTableData = (await this.baseService.executeCommand<any>(gridDataRequest, { ownerId, ...gridDataReqestPayload }).toPromise()).data;

    let dataTable: DataTable = {
      tableConfig: dataTableConfig,
      tableData: dataTableData
    };

    return dataTable;
  }

  getTableRequest(searchCriteriaRequest: string, gridConfigRequest: string, gridDataRequest: string): TableRequest {
    return {
      searchCriteriaConfigRequest: {
        url: this.helperService.apiUrl(searchCriteriaRequest),
        resultField: 'data',
      },
      tableConfigRequest: {
        url: this.helperService.apiUrl(gridConfigRequest),
        resultField: 'data',
      },
      tableDataRequest: {
        url: this.helperService.apiUrl(gridDataRequest),
        resultField: 'data',
      },
    }
  }

  async getDataTableTabGroup(tabConfigRequest: string): Promise<TabConfig[]> {
    let tabConfig = (await this.baseService.executeCommand<TabConfig[]>(tabConfigRequest, {}).toPromise()).data;
    tabConfig.forEach(tab => {
      tab.configUrl = this.helperService.apiUrl(tab.configUrl);
      tab.dataUrl = this.helperService.apiUrl(tab.dataUrl);
    });
    return tabConfig;
  }

  async getDetailSectionConfig(detailSectionRequest: string, id: number | string): Promise<DetailSectionConfig> {
    let config = (await this.baseService.executeCommand<DetailSectionConfig>(detailSectionRequest, { id }).toPromise()).data;
    return config;
  }

  async getDataTableMasterDetail(searchCriteriaRequest: string, gridConfigRequest: string, gridDataRequest: string, tabConfigRequest: string): Promise<DataTableMasterDetail> {
    let tableRequest = this.getTableRequest(searchCriteriaRequest, gridConfigRequest, gridDataRequest);
    let tabConfig = tabConfigRequest ? (await this.getDataTableTabGroup(tabConfigRequest)) : null;

    return {
      searchCriteriaConfigRequest: tableRequest.searchCriteriaConfigRequest,
      tableConfigRequest: tableRequest.tableConfigRequest,
      tableDataRequest: tableRequest.tableDataRequest,
      tabConfig: tabConfig
    }
  }

  async getDetailSectionMasterDetail(id: number | string, detailSectionRequest: string, tabConfigRequest: string): Promise<DetailSectionMasterDetail> {
    let detailSectionConfig = (await this.getDetailSectionConfig(detailSectionRequest, id));
    let tabConfig = tabConfigRequest ? (await this.getDataTableTabGroup(tabConfigRequest)) : null;

    return {
      detailSectionConfig: detailSectionConfig,
      tabConfig: tabConfig
    }

    return null;
  }

  getReportRequest(reportsRequest: string): Request {
    return {
      url: this.helperService.apiUrl(reportsRequest),
      resultField: 'data',
    };
  }

  get customDialogMapper() {
    return {
      UserMenuPreviewDialogComponent: UserMenuPreviewDialogComponent,
      OutgoingResultBinariesDialogComponent: OutgoingResultBinariesDialogComponent,
      IncomingMessageBinariesDialogComponent: IncomingMessageBinariesDialogComponent,
      SalesOrderCustomerDialogComponent: SalesOrderCustomerDialogComponent,
      SalesOrderCreateDialogComponent: SalesOrderCreateDialogComponent,
      SctConfirmDialogComponent: SctConfirmDialogComponent,
      GtrReceiveDialogComponent: GtrReceiveDialogComponent,
      SctSearchStockCodeDialogComponent: SctSearchStockCodeDialogComponent,
      ActionEftRequestDialogComponent: ActionEftRequestDialogComponent,
      FinanceFreeMoneyToAccountDialogComponent: FinanceFreeMoneyToAccountDialogComponent,
      PosCashUpReportDialogComponent: PosCashUpReportDialogComponent,
      PosOperatorTransactionsReportDialogComponent: PosOperatorTransactionsReportDialogComponent,
      PosAccountPayWithMoneyEquipmentDialogComponent: PosAccountPayWithMoneyEquipmentDialogComponent,
      CallbackActionDialogComponent: CallbackActionDialogComponent,
      SurveyActionDialogComponent: SurveyActionDialogComponent,
      EmailFailedDeliveryActionDialogComponent: EmailFailedDeliveryActionDialogComponent,
      ToDoListItemActionDialogComponent: ToDoListItemActionDialogComponent
    };
  }
}
